import { createContext, useContext } from "react";

import type { CVDocument } from "../document/model";
import type { RespuestaSugerencias, Sugerencia } from "../suggestions/types";

/**
 * Por donde viaja el documento: lo unico del editor que depende del servidor.
 *
 * El editor vive en dos sitios. En creatorhv habla directamente con Directus,
 * sin sesion. En la suite de personas habla con el backend del landing, que
 * tiene la sesion de la persona y comprueba que cada hoja de vida es suya.
 * Todo lo demas -- el reductor, el autoguardado, las sugerencias, la hoja --
 * es igual en los dos, y por eso no sabe cual de los dos tiene debajo.
 *
 * Los errores se lanzan como `ErrorApi` (lib/api/errores.ts), con `status` 0
 * cuando no hubo respuesta.
 */
export interface CvTransporte {
  /**
   * Guarda el documento y devuelve como quedo en el servidor.
   *
   * `payload` es la salida de `toDirectus`: la forma de Directus, con las
   * relaciones como create/update/delete. `omitidos` son los campos que el
   * servidor no acepto -- se guardo todo menos eso.
   */
  guardar(
    cvId: number,
    payload: Record<string, unknown>,
  ): Promise<{ doc: CVDocument; omitidos: string[] }>;

  /** Las sugerencias ya generadas para este CV, o null si nunca se pidieron. */
  sugerenciasGuardadas(cvId: number): Promise<{ sugerencias: Sugerencia[] } | null>;

  /** Genera sugerencias para el documento tal como esta en pantalla. */
  generarSugerencias(doc: CVDocument): Promise<RespuestaSugerencias>;

  /**
   * Registra la decision sobre una sugerencia. Puede fallar: quien llama no
   * debe dejar que eso interrumpa la revision.
   */
  marcarSugerencia(
    cvId: number,
    sid: string,
    estado: Sugerencia["estado"],
    final?: string,
  ): Promise<void>;
}

export const TransporteContexto = createContext<CvTransporte | null>(null);

export const useTransporte = (): CvTransporte => {
  const transporte = useContext(TransporteContexto);
  // Falla fuerte y pronto: un editor sin transporte parece funcionar hasta el
  // primer guardado, y ahi pierde lo que la persona escribio.
  if (!transporte) throw new Error("El editor necesita un <TransporteContexto.Provider>.");
  return transporte;
};
